/** MemberWorkbench — MY DESK + MY WALL investigator workspace.
 *  Evolves the existing Member Desk; reuses all existing services. */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Lock, Move, RotateCcw, Eye, EyeOff, Crosshair } from "lucide-react";
import { useMemberDesk } from "../hooks/useMemberDesk";
import { desk as dk, fontMicro } from "../components/member-desk/tokens";
import OracleBar from "../components/oracle/OracleBar";
import CaseFilesSection from "../components/member-desk/CaseFilesSection";
import { Bookmarks } from "../components/member-desk/ContinueReading";
import DeskHeader from "../components/member-desk/DeskHeader";
import { useMemberAuth } from "../hooks/useMemberAuth";
import { getWorkbench, saveDesk, resetDesk, setInvestigation } from "./api";
import { DispatchStation, FieldNotes, ArchiveSearch, EvidenceTray, RecentActivity, ModuleShell } from "./DeskModules";
import Wall from "./Wall";
import { Terminal, Bookmark } from "lucide-react";

const MODULE_META = {
  oracle:          { label: "Oracle Terminal" },
  dispatch:        { label: "Dispatch Station" },
  case_files:      { label: "Active Case Files" },
  evidence_tray:   { label: "Evidence Tray" },
  field_notes:     { label: "Field Notes" },
  recent_activity: { label: "Recent Activity" },
  search:          { label: "Archive Search" },
  watchlist:       { label: "Watchlist / Saved" },
};

const MemberWorkbench = ({ member }) => {
  const { desk: data, loading, unbookmark } = useMemberDesk();
  const { logout } = useMemberAuth();
  const [tab, setTab] = useState("desk");
  const [wb, setWb] = useState(null);
  const [arranging, setArranging] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const dragId = useRef(null);
  const saveTimer = useRef(null);

  useEffect(() => {
    getWorkbench().then(setWb).catch(() => {
      // Workspace sync failure must NEVER eject the member or block the
      // Members Area — fall back to the default Desk locally; the server
      // copy re-syncs on the next successful load/save.
      setWb({
        version: 1,
        desk: { locked: true, modules: Object.keys(MODULE_META).map((id, i) => ({ id, hidden: false, order: i, size: "auto" })) },
        current_investigation: null, wall_pin_count: 0, _fallback: true,
      });
      toast.error("Workspace sync unavailable — showing your default Desk.");
    });
  }, []);

  const modules = useMemo(() => {
    const list = (wb?.desk?.modules || []).slice().sort((a, b) => a.order - b.order);
    return list;
  }, [wb]);
  const hidden = modules.filter((m) => m.hidden);

  const persist = useCallback((mods, locked) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveDesk(mods.map((m, i) => ({ ...m, order: i })), locked).catch(() => toast.error("Desk save failed"));
    }, 600);
  }, []);

  const setModules = (next, locked = !arranging) => {
    setWb((w) => ({ ...w, desk: { ...w.desk, modules: next.map((m, i) => ({ ...m, order: i })) } }));
    persist(next, locked);
  };

  const move = (id, dir) => {
    const vis = modules.filter((m) => !m.hidden);
    const idx = vis.findIndex((m) => m.id === id);
    const to = idx + dir;
    if (to < 0 || to >= vis.length) return;
    const next = vis.slice();
    [next[idx], next[to]] = [next[to], next[idx]];
    setModules([...next, ...hidden]);
  };

  const toggleHidden = (id) => {
    const next = modules.map((m) => m.id === id ? { ...m, hidden: !m.hidden } : m);
    setModules(next);
  };

  const onDrop = (targetId) => {
    if (!dragId.current || dragId.current === targetId) return;
    const vis = modules.filter((m) => !m.hidden);
    const from = vis.findIndex((m) => m.id === dragId.current);
    const to = vis.findIndex((m) => m.id === targetId);
    if (from < 0 || to < 0) return;
    const next = vis.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    setModules([...next, ...hidden]);
    dragId.current = null;
  };

  const doReset = async () => {
    if (!window.confirm("Reset Desk layout? Your pins, notes and saved material are kept.")) return;
    const r = await resetDesk();
    setWb((w) => ({ ...w, desk: r.desk }));
    setArranging(false);
    toast.success("Desk reset — content untouched");
  };

  const clearInvestigation = async () => {
    await setInvestigation(null);
    setWb((w) => ({ ...w, current_investigation: null }));
  };

  const inv = wb?.current_investigation;

  const render = (m) => {
    switch (m.id) {
      case "oracle": return (
        <ModuleShell id="oracle" title="Oracle Terminal" icon={Terminal} accent={dk.redact}><OracleBar /></ModuleShell>);
      case "dispatch": return <DispatchStation />;
      case "case_files": return (
        <section data-testid="desk-module-case_files">
          {inv && (
            <div className="border px-4 py-2.5 mb-2 flex items-center gap-3" data-testid="current-investigation-banner"
              style={{ borderColor: dk.redact, background: "#140b0a" }}>
              <Crosshair size={12} style={{ color: dk.redact }} />
              <span className={fontMicro} style={{ color: dk.redact }}>Current Investigation</span>
              <Link to={inv.route || "#"} className="text-[12px] truncate" style={{ color: dk.ink }}>{inv.title}</Link>
              <button onClick={clearInvestigation} data-testid="clear-investigation"
                className={`${fontMicro} ml-auto`} style={{ color: dk.inkMuted }}>Clear</button>
            </div>
          )}
          <CaseFilesSection caseFiles={data?.case_files || []} />
        </section>);
      case "evidence_tray": return <EvidenceTray />;
      case "field_notes": return <FieldNotes />;
      case "recent_activity": return <RecentActivity items={data?.continue_reading || []} />;
      case "search": return <ArchiveSearch />;
      case "watchlist": return (
        <ModuleShell id="watchlist" title="Watchlist / Saved" icon={Bookmark} accent="#5DD4B3">
          <Bookmarks items={data?.bookmarks || []} onRemove={unbookmark} />
        </ModuleShell>);
      default: return null;
    }
  };

  if (loading || !wb) {
    return <div className="min-h-[40vh] flex items-center justify-center">
      <span className={fontMicro} style={{ color: dk.inkMuted }}>OPENING WORKBENCH…</span></div>;
  }

  return (
    <div data-testid="member-workbench">
      <DeskHeader identity={data?.identity} clearance={data?.clearance} stats={data?.stats} onLogout={logout} />

      {/* Tabs + DESK menu — one compact control row */}
      <div className="flex items-center gap-1 mt-4 mb-4 border-b" style={{ borderColor: dk.edge }}>
        {[["desk", "MY DESK"], ["wall", "MY WALL"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`workbench-tab-${id}`}
            className={`px-4 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.3em] border-b-2 ${
              tab === id ? "border-[#D92D20] text-[#E4DFD3]" : "border-transparent text-[#6F6A60] hover:text-[#A39E93]"}`}>
            {label}{id === "wall" && wb.wall_pin_count ? ` · ${wb.wall_pin_count}` : ""}
          </button>
        ))}
        {tab === "desk" && (
          <div className="relative ml-auto">
            <button onClick={() => setMenuOpen((v) => !v)} data-testid="desk-menu-btn"
              className={`${fontMicro} flex items-center gap-1 px-3 py-2`} style={{ color: dk.inkDim }}>
              DESK <ChevronDown size={11} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full z-40 border w-52 py-1" data-testid="desk-menu"
                style={{ background: dk.surface, borderColor: dk.edgeHi }}>
                {!arranging ? (
                  <button onClick={() => { setArranging(true); setMenuOpen(false); }} data-testid="desk-arrange"
                    className={`${fontMicro} w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/5`} style={{ color: dk.ink }}>
                    <Move size={11} /> Arrange Desk</button>
                ) : (
                  <button onClick={() => { setArranging(false); setMenuOpen(false); persist(modules, true); }} data-testid="desk-lock"
                    className={`${fontMicro} w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/5`} style={{ color: dk.go }}>
                    <Lock size={11} /> Lock Desk</button>
                )}
                <div className={`${fontMicro} px-3 pt-2 pb-1`} style={{ color: dk.inkMuted }}>Hidden Modules</div>
                {Object.keys(MODULE_META).map((id) => {
                  const m = modules.find((x) => x.id === id);
                  const isHidden = m?.hidden;
                  return (
                    <button key={id} onClick={() => toggleHidden(id)} data-testid={`desk-toggle-${id}`}
                      className="w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-white/5 font-mono text-[10px]"
                      style={{ color: isHidden ? dk.inkMuted : dk.inkDim }}>
                      {isHidden ? <EyeOff size={10} /> : <Eye size={10} />} {MODULE_META[id].label}
                    </button>);
                })}
                <button onClick={doReset} data-testid="desk-reset"
                  className={`${fontMicro} w-full text-left px-3 py-2 flex items-center gap-2 hover:bg-white/5 border-t mt-1`}
                  style={{ color: dk.redact, borderColor: dk.edge }}>
                  <RotateCcw size={11} /> Reset Desk</button>
              </div>
            )}
          </div>
        )}
      </div>

      {tab === "wall" ? <Wall onPinCountChange={(n) => setWb((w) => ({ ...w, wall_pin_count: n }))} /> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="desk-grid">
          {modules.filter((m) => !m.hidden).map((m, i, vis) => (
            <div key={m.id}
              draggable={arranging}
              onDragStart={() => { dragId.current = m.id; }}
              onDragOver={(e) => arranging && e.preventDefault()}
              onDrop={() => arranging && onDrop(m.id)}
              className={(m.id === "oracle" || m.id === "case_files") ? "lg:col-span-2" : ""}
              style={arranging ? { outline: `1px dashed ${dk.edgeHi}`, cursor: "grab",
                                   transition: "box-shadow .15s", boxShadow: "0 6px 18px rgba(0,0,0,.45)" } : undefined}
              data-testid={`desk-slot-${m.id}`}>
              {arranging && (
                <div className="flex items-center gap-2 px-2 py-1" style={{ background: "#131118" }}>
                  <span className={fontMicro} style={{ color: dk.inkMuted }}>{MODULE_META[m.id]?.label}</span>
                  <button onClick={() => move(m.id, -1)} disabled={i === 0} data-testid={`move-up-${m.id}`}
                    className="ml-auto disabled:opacity-20" style={{ color: dk.inkDim }} aria-label="Move up"><ChevronUp size={13} /></button>
                  <button onClick={() => move(m.id, 1)} disabled={i === vis.length - 1} data-testid={`move-down-${m.id}`}
                    className="disabled:opacity-20" style={{ color: dk.inkDim }} aria-label="Move down"><ChevronDown size={13} /></button>
                </div>
              )}
              {render(m)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MemberWorkbench;
